import { c as _c } from "react/compiler-runtime";
// A block of labelled numbers, rendered as TEXT by default and as a bar chart
// on demand.
//
// This is the founder's central complaint made concrete: «вообще мне не нужны
// вот эти графики… сделайте мне в текстовом виде и перевернуть в график».
// Charts read as decoration; the numbers are what she works from. So the text
// form is primary and identical data drives both.
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";
import type { StatBlock as StatBlockData, StatTone } from "@/api";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { axisProps, ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { formatNumber } from "@osmix/formatters";
import { cn } from "@/lib/utils";
export type StatView = "text" | "chart";
const TONE_TEXT: Record<StatTone, string> = {
  default: "",
  warning: "text-amber-600 dark:text-amber-500",
  danger: "text-destructive"
};
const TONE_FILL: Record<StatTone, string> = {
  default: "var(--chart-1, #6366f1)",
  warning: "var(--chart-4, #f59e0b)",
  danger: "var(--destructive, #ef4444)"
};

/** Bar tint for the inline proportion track in text view. */
const TONE_BAR: Record<StatTone, string> = {
  default: "bg-primary/30",
  warning: "bg-amber-500/50",
  danger: "bg-destructive/50"
};
export function StatBlock(t0) {
  const $ = _c(30);
  const {
    block,
    view,
    title,
    drillLabel,
    labelPrefix,
    noteKey
  } = t0;
  const {
    t
  } = useTranslation();
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  if ($[0] !== block.noteKey || $[1] !== block.noteTone || $[2] !== block.rows || $[3] !== labelPrefix || $[4] !== noteKey || $[5] !== t || $[6] !== title || $[7] !== view) {
    let t4;
    if ($[13] !== labelPrefix || $[14] !== t) {
      t4 = row => ({
        ...row,
        label: row.label ?? t(`${labelPrefix}.${row.key}`, row.key)
      });
      $[13] = labelPrefix;
      $[14] = t;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    const rows = block.rows.map(t4);
    const max = Math.max(...rows.map(_temp), 1);
    T1 = Card;
    if ($[16] !== title) {
      t3 = <CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader>;
      $[16] = title;
      $[17] = t3;
    } else {
      t3 = $[17];
    }
    T0 = CardContent;
    t1 = "flex flex-col gap-3";
    t2 = view === "text" ? <dl className="flex flex-col">{rows.map(row_1 => <div key={row_1.key} className="flex flex-col gap-1 border-b py-2 last:border-0"><div className="flex items-baseline justify-between gap-4"><dt className="text-sm text-muted-foreground">{row_1.label}</dt><dd className="flex items-baseline gap-2 text-right"><span className={cn("text-lg font-semibold tabular-nums", TONE_TEXT[row_1.tone ?? "default"])}>{formatNumber(row_1.value)}</span>{row_1.note !== undefined && (block.noteKey ?? noteKey) && <span className={cn("text-xs", block.noteTone ? TONE_TEXT[block.noteTone] || "text-muted-foreground" : "text-destructive")}>{t(block.noteKey ?? noteKey as string, {
                count: row_1.note
              })}</span>}</dd></div><div className="h-1 w-full overflow-hidden rounded-full bg-muted"><div className={cn("h-full rounded-full", TONE_BAR[row_1.tone ?? "default"])} style={{
            width: `${Math.max(2, row_1.value / max * 100)}%`
          }} /></div></div>)}</dl> : <ChartContainer height={240}><BarChart data={rows} layout="vertical" margin={{
        left: 24,
        right: 16
      }}><CartesianGrid horizontal={false} stroke="var(--border)" /><XAxis type="number" {...axisProps} /><YAxis type="category" dataKey="label" width={150} {...axisProps} /><ChartTooltip config={{
          value: {
            label: title,
            color: "#6366f1"
          }
        }} /><Bar dataKey="value" radius={[0, 4, 4, 0]} isAnimationActive={false}>{rows.map(_temp2)}</Bar></BarChart></ChartContainer>;
    $[0] = block.noteKey;
    $[1] = block.noteTone;
    $[2] = block.rows;
    $[3] = labelPrefix;
    $[4] = noteKey;
    $[5] = t;
    $[6] = title;
    $[7] = view;
    $[8] = T0;
    $[9] = T1;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    T0 = $[8];
    T1 = $[9];
    t1 = $[10];
    t2 = $[11];
    t3 = $[12];
  }
  let t4;
  if ($[18] !== block.href || $[19] !== drillLabel) {
    t4 = block.href && drillLabel && <Button asChild={true} variant="ghost" size="sm" className="justify-start px-0 text-muted-foreground"><Link to={block.href}>{drillLabel}<ArrowRight className="size-3.5" /></Link></Button>;
    $[18] = block.href;
    $[19] = drillLabel;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== T0 || $[22] !== t1 || $[23] !== t2 || $[24] !== t4) {
    t5 = <T0 className={t1}>{t2}{t4}</T0>;
    $[21] = T0;
    $[22] = t1;
    $[23] = t2;
    $[24] = t4;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  let t6;
  if ($[26] !== T1 || $[27] !== t3 || $[28] !== t5) {
    t6 = <T1>{t3}{t5}</T1>;
    $[26] = T1;
    $[27] = t3;
    $[28] = t5;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  return t6;
}
function _temp2(row_2) {
  return <Cell key={row_2.key} fill={TONE_FILL[row_2.tone ?? "default"]} />;
}
function _temp(row_0) {
  return row_0.value;
}